import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { I18nextProvider } from 'react-i18next'
import i18n from './i18n'
import { ChatBotProvider } from './contexts/ChatBotContext'
import { CookieConsentProvider } from './contexts/CookieConsentContext'
import { CookieConsent } from './components/CookieConsent'
import { ScrollToTop } from './components/ScrollToTop'
import { Layout } from './pages/Layout'
import { HomePage } from './pages/HomePage'
import { CompanyPage } from './pages/CompanyPage'
import { ProductsPage } from './pages/ProductsPage'
import { ContactsPage } from './pages/ContactsPage'
import { SustainabilityPage } from './pages/SustainabilityPage'
import { OpenDoorsPage } from './pages/OpenDoorsPage'
import { PrivacyPage } from './pages/PrivacyPage'
import { CookiesPage } from './pages/CookiesPage'
import './styles/globals.css'

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <I18nextProvider i18n={i18n}>
        <CookieConsentProvider>
          <ChatBotProvider>
            <Routes>
              {/* Redirect root to default language */}
              <Route path="/" element={<Navigate to="/it" replace />} />

              {/* Language-prefixed routes */}
              <Route path="/:lang/*" element={<LanguageWrapper />} />
            </Routes>
            <CookieConsent />
          </ChatBotProvider>
        </CookieConsentProvider>
      </I18nextProvider>
    </BrowserRouter>
  )
}

const pageVariants = {
  initial: {
    opacity: 0,
    y: 10,
  },
  animate: {
    opacity: 1,
    y: 0,
  },
  exit: {
    opacity: 0,
    y: -10,
  },
}

const pageTransition = {
  duration: 0.3,
  ease: [0.25, 0.1, 0.25, 1] as const,
}

function LanguageWrapper() {
  const location = useLocation()
  // Use path after /:lang/ as key so language switches don't trigger
  // exit animations with mismatched base paths
  const pageKey = location.pathname.split('/').slice(2).join('/') || 'index'

  return (
    <Layout>
      <AnimatePresence mode="wait">
        <motion.div
          key={pageKey}
          initial="initial"
          animate="animate"
          exit="exit"
          variants={pageVariants}
          transition={pageTransition}
          className="w-full"
        >
          <Routes>
            <Route index element={<HomePage />} />
            {/* Company */}
            <Route path="lazienda" element={<CompanyPage />} />
            <Route path="the-company" element={<CompanyPage />} />
            <Route path="lentreprise" element={<CompanyPage />} />
            <Route path="la-empresa" element={<CompanyPage />} />
            <Route path="firma" element={<CompanyPage />} />
            {/* Products */}
            <Route path="prodotti" element={<ProductsPage />} />
            <Route path="products" element={<ProductsPage />} />
            <Route path="produits" element={<ProductsPage />} />
            <Route path="productos" element={<ProductsPage />} />
            <Route path="produkty" element={<ProductsPage />} />
            {/* Contacts */}
            <Route path="contatti" element={<ContactsPage />} />
            <Route path="contacts" element={<ContactsPage />} />
            <Route path="contactos" element={<ContactsPage />} />
            <Route path="kontakt" element={<ContactsPage />} />
            {/* Sustainability */}
            <Route path="sostenibilita" element={<SustainabilityPage />} />
            <Route path="sustainability" element={<SustainabilityPage />} />
            <Route path="durabilite" element={<SustainabilityPage />} />
            <Route path="sostenibilidad" element={<SustainabilityPage />} />
            <Route path="zrownowazona-produkcja" element={<SustainabilityPage />} />
            {/* Open Doors */}
            <Route path="porte-aperte-in-bonifanti" element={<OpenDoorsPage />} />
            <Route path="open-doors" element={<OpenDoorsPage />} />
            <Route path="portes-ouvertes" element={<OpenDoorsPage />} />
            <Route path="puertas-abiertas" element={<OpenDoorsPage />} />
            <Route path="drzwi-otwarte" element={<OpenDoorsPage />} />
            {/* Legal */}
            <Route path="privacy" element={<PrivacyPage />} />
            <Route path="cookie-policy" element={<CookiesPage />} />
          </Routes>
        </motion.div>
      </AnimatePresence>
    </Layout>
  )
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
)
